import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bars3Icon, BookmarkIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DEFAULT_REGION, buildRegionPath, useActiveRegion } from '../regions';
import NotificationBell from './NotificationBell';

interface NavItem {
  name: string;
  href: string;
  badge?: 'PRO';
}

const navClass = ({ isActive }: { isActive: boolean }) =>
  `inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
    isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
  }`;

const badgeClass = 'inline-flex items-center px-1.5 py-0.5 text-[11px] font-semibold rounded-full bg-blue-600 text-white';

const Navigation: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const activeRegion = useActiveRegion() ?? DEFAULT_REGION;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const regionPath = (path?: string) => buildRegionPath(activeRegion, path);

  const authenticatedNav: NavItem[] = [
    { name: 'My Feed', href: regionPath('home'), badge: user?.is_premium ? 'PRO' : undefined },
    { name: 'Browse Opportunities', href: regionPath('opportunities') },
    { name: 'Insights', href: '/insights' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
    { name: 'About', href: '/about' },
  ];

  const publicNav: NavItem[] = [
    { name: 'My Feed', href: regionPath('home') },
    { name: 'Browse Opportunities', href: regionPath('opportunities') },
    { name: 'Insights', href: '/insights' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
    { name: 'About', href: '/about' },
  ];

  useEffect(() => {
    const onClickAway = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', onClickAway);
    }

    return () => document.removeEventListener('mousedown', onClickAway);
  }, [dropdownOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navItems = isAuthenticated ? authenticatedNav : publicNav;

  const normalize = (path: string) => {
    if (!path) return '/';
    const trimmed = path.replace(/\/+$/, '');
    return trimmed.length > 0 ? trimmed : '/';
  };

  const normalizedPath = normalize(location.pathname);

  const renderNavLinks = (className?: string) => (
    <div className={className}>
      {navItems.map((item) => {
        const itemPath = normalize(item.href);
        const isActive = normalizedPath === itemPath || (itemPath !== '/' && normalizedPath.startsWith(`${itemPath}/`));
        return (
          <Link key={item.name} to={item.href} className={navClass({ isActive })}>
            <span>{item.name}</span>
            {item.badge && <span className={badgeClass}>{item.badge}</span>}
          </Link>
        );
      })}
    </div>
  );

  const UserBadge = () => (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900">
          {`Welcome, ${user?.full_name ?? 'User'}`}
        </span>
        {user?.is_premium && <span className={badgeClass}>PRO</span>}
      </div>
      <button
        onClick={() => setDropdownOpen((prev) => !prev)}
        className={`relative flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
          dropdownOpen ? 'border-blue-500 bg-blue-50' : 'border-transparent bg-gray-100 hover:bg-gray-200'
        }`}
        aria-label="Account options"
      >
        <span className="text-sm font-semibold text-gray-700">
          {user?.full_name?.charAt(0) ?? 'U'}
        </span>
      </button>
    </div>
  );

  const dropdownItems = [
    { label: 'Saved Opportunities', to: regionPath('saved') },
    { label: 'Account', to: '/account' },
    { label: 'Match Preferences', to: '/preferences', badge: 'PRO' },
    ...(user?.role === 'admin' ? [{ label: 'Open CMS', to: '/grandslam/blog' }] : []),
    ...(user?.role === 'admin' ? [{ label: 'Admin Panel', to: '/grandslam' }] : []),
  ];

  // Brand link: signed-in users go to their default destinations
  const brandTarget = isAuthenticated
    ? (user?.is_premium ? '/home' : '/opportunities')
    : '/';

  return (
    <nav className="relative z-50 bg-white border-b border-gray-200">
      <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex flex-1 items-center gap-10">
          <Link to={brandTarget} className="text-2xl font-semibold text-gray-900">
            OppFoundry
          </Link>
          <div className="hidden md:flex items-center gap-4">
            {renderNavLinks('flex items-center gap-2')}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isAuthenticated ? (
            <div className="relative flex items-center gap-3" ref={dropdownRef}>
              <Link
                to={regionPath('saved')}
                className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition ${
                  normalizedPath === normalize(regionPath('saved'))
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <BookmarkIcon className="h-4 w-4" />
                Saved
              </Link>
              <UserBadge />
              <NotificationBell />
              {dropdownOpen && (
                <div className="absolute right-0 top-full z-50 mt-3 w-52 rounded-lg border border-gray-100 bg-white py-2 shadow-lg">
                  {dropdownItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <span>{item.label}</span>
                      {item.badge && <span className={badgeClass}>{item.badge}</span>}
                    </Link>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="mt-1 w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-gray-900">
                Sign In
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          {isAuthenticated && <NotificationBell />}
          <button
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="space-y-1 px-4 py-3">
            {navItems.map((item) => {
              const itemPath = normalize(item.href);
              const isActive = normalizedPath === itemPath || (itemPath !== '/' && normalizedPath.startsWith(`${itemPath}/`));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center justify-between rounded-md px-3 py-2 text-base font-medium ${
                    isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{item.name}</span>
                  {item.badge && <span className={badgeClass}>{item.badge}</span>}
                </Link>
              );
            })}
          </div>
          <div className="border-t border-gray-100 px-4 py-3">
            {isAuthenticated ? (
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{user?.full_name}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                  {user?.is_premium && <span className={`${badgeClass} mt-2 inline-flex`}>PRO</span>}
                </div>
                <div className="space-y-1">
                  {dropdownItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <span>{item.label}</span>
                      {item.badge && <span className={badgeClass}>{item.badge}</span>}
                    </Link>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="mt-1 w-full rounded-md px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Link
                  to="/login"
                  className="rounded-md border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="rounded-md bg-blue-600 px-3 py-2 text-center text-sm font-semibold text-white hover:bg-blue-500"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
